export interface SelectableMembership {
  workspace_id: string;
  workspaces?: unknown;
}

export interface WorkspaceChoice {
  /** Named by the checkout or an accepted invitation. Always wins when it is a membership. */
  preferredWorkspaceId?: string | null;
  /** The workspace the person chose on this device. */
  savedWorkspaceId?: string | null;
}

const usable = <T extends SelectableMembership>(memberships: readonly T[]): T[] =>
  memberships.filter((membership) => !!membership.workspaces);

/**
 * Whether the main-workspace rule must be asked. Only when more than one
 * workspace could open and neither a checkout nor a saved choice settles it.
 */
export function needsMainWorkspace<T extends SelectableMembership>(
  memberships: readonly T[],
  choice: WorkspaceChoice,
): boolean {
  const candidates = usable(memberships);
  if (candidates.length <= 1) return false;
  if (choice.preferredWorkspaceId) return false;
  return !candidates.some((membership) => membership.workspace_id === choice.savedWorkspaceId);
}

/**
 * The membership the app opens when there is no explicit switch: the checkout
 * or invitation workspace, then the saved choice, then the main workspace under
 * the rule. With several workspaces and no answer from the rule, nothing opens:
 * the app never guesses the first one.
 */
export function chooseActiveMembership<T extends SelectableMembership>(
  memberships: readonly T[],
  choice: WorkspaceChoice & { mainWorkspaceId?: string | null },
): T | undefined {
  const candidates = usable(memberships);
  const named = (id?: string | null) => (id ? candidates.find((membership) => membership.workspace_id === id) : undefined);
  return named(choice.preferredWorkspaceId)
    ?? named(choice.savedWorkspaceId)
    ?? named(choice.mainWorkspaceId)
    ?? (candidates.length === 1 ? candidates[0] : undefined);
}
